import { useEffect } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Icon } from './Icon';

interface Props {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  readOnly?: boolean;
}

/** Existing notes were stored as plain text. Wrap them so line breaks
 *  render properly in the rich editor. HTML content passes through. */
const ensureHtml = (raw: string): string => {
  if (!raw) return '';
  if (/<[a-z][^>]*>/i.test(raw)) return raw;
  return raw
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const ToolbarButton = ({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    className="rt-tb-btn"
    data-active={active ? 'true' : undefined}
    onClick={(e) => {
      // Keep editor focus — buttons are inside the same form
      e.preventDefault();
      onClick();
    }}
    title={title}
    aria-label={title}
  >
    {children}
  </button>
);

const Toolbar = ({ editor }: { editor: Editor }) => {
  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <div className="rt-toolbar">
      <ToolbarButton
        title="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <strong style={{ fontSize: 12 }}>B</strong>
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <em style={{ fontSize: 12 }}>I</em>
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <span style={{ fontSize: 12, textDecoration: 'line-through' }}>S</span>
      </ToolbarButton>
      <span className="rt-tb-sep" />
      <ToolbarButton
        title="Heading 2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <span style={{ fontSize: 11, fontWeight: 600 }}>H2</span>
      </ToolbarButton>
      <ToolbarButton
        title="Heading 3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <span style={{ fontSize: 11, fontWeight: 600 }}>H3</span>
      </ToolbarButton>
      <span className="rt-tb-sep" />
      <ToolbarButton
        title="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <span style={{ fontSize: 12 }}>•</span>
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <span style={{ fontSize: 11 }}>1.</span>
      </ToolbarButton>
      <span className="rt-tb-sep" />
      <ToolbarButton
        title="Inline code"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{'<>'}</span>
      </ToolbarButton>
      <ToolbarButton
        title="Link"
        active={editor.isActive('link')}
        onClick={setLink}
      >
        <Icon name="link" size={12} />
      </ToolbarButton>
    </div>
  );
};

export const RichTextEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly = false,
}: Props) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content: ensureHtml(value),
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'rt-content',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML();
      onChange(html);
    },
    onBlur: () => {
      if (onBlur) onBlur();
    },
  });

  // Sync external value changes (e.g. after a save round-trip) without
  // wiping the cursor on every keystroke
  useEffect(() => {
    if (!editor) return;
    const incoming = ensureHtml(value);
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (incoming !== current) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !readOnly) editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rt-wrap" data-readonly={readOnly ? 'true' : undefined}>
      {!readOnly && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
